import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { extractErrorMessageFromResponse } from "../utils";
import { getSubpathBridgeClientFetch } from "@zapier/identity/client/fetch";
import * as z from "zod";
import { listTableFieldsResponseSchema } from "./schemas/tablesApiSchemas";

const tablesClient = getSubpathBridgeClientFetch("tables");

export type TableFieldsFetchResult = {
  data: z.infer<typeof listTableFieldsResponseSchema>["data"];
};

/**
 * Fetches table fields via the bridge client to Tables API (`/api/v1/tables/:id/fields`).
 * This endpoint will return 200 when the user has access to the table fields.
 */
export const tableFieldsAtomFamily_SUSPENDS = atomFamily(
  (tableId: string | undefined) => {
    const tableFieldsAtom = atom(
      async (): Promise<TableFieldsFetchResult | null> => {
        if (!tableId) {
          return null;
        }

        try {
          const res: unknown = await tablesClient(
            `/v1/tables/${tableId}/fields`,
          );

          if (!(res instanceof Response)) {
            console.error(
              `Unknown response type from bridge client for table fields ${tableId}`,
            );
            return null;
          }

          if (res.ok) {
            const json: unknown = await res.json();
            const data = listTableFieldsResponseSchema.parse(json).data;
            return { data };
          } else {
            /* ----- 5xx or other unexpected errors – log & degrade ----- */
            const errorMessage = await extractErrorMessageFromResponse(res);
            console.error(
              `Failed to fetch table fields for table ${tableId}: ${errorMessage}`,
            );
            return null;
          }
        } catch (error) {
          if (error instanceof z.ZodError) {
            const prettyError = z.prettifyError(error);
            console.error(
              `Table fields API response validation failed for table ${tableId}:`,
              prettyError,
            );
          } else {
            console.error(
              `Error fetching table fields for table ${tableId}:`,
              error,
            );
          }
          return null;
        }
      },
    );

    tableFieldsAtom.debugLabel = `tableFieldsAtomFamily_SUSPENDS(${tableId})`;
    return tableFieldsAtom;
  },
);
